// ============================================================================
// ANALYSIS — business intelligence over the quote book.
// ----------------------------------------------------------------------------
// The Run Sheet dispatches; this page is where you SEE the business.
//
// Slice 1 of the binding spec (docs/design/SPEC-analysis-page.md). The page is
// now a RENDERER, not a chart library: it derives rows once, then walks a report
// definition and hands each entry to AnalysisPanel. There is no panel-specific
// code in this file and there must never be, because the moment one panel earns
// a special case here, every future panel needs one too and the registry decays
// into a switch statement. A new panel is one file plus one registry entry.
//
// Founding constraint, unchanged from the first version and more important than
// any of the layout: honesty about sample size. With a handful of decided quotes
// out of a hundred-odd, a win rate is arithmetic on noise. Panels declare their
// own readiness and say what would switch them on. No fabricated numbers, ever.
//
// Every figure derives from the same helpers as the Run Sheet (laneOf,
// quoteValueExGst, classifyProjectCategories, matchesRunSheetSearch) through
// services/analysis/model, so the two pages cannot disagree about a project.
// Zero new endpoints.
// ============================================================================

import { rjBirdOrDemoText } from '../services/demo-identity';
import React, { useMemo, useState, useEffect } from 'react';
import { Icons } from '../components/ui/Icon';
import AnalysisPanel from '../components/analysis/AnalysisPanel';
import EvidenceDrawer from '../components/analysis/EvidenceDrawer';
import {
    ANALYSIS_PANELS,
    BUILTIN_REPORT,
    type AnalysisContext,
    type Cell,
    type PanelDef,
} from '../services/analysis/registry';
import {
    deriveRows,
    buildWindowStamp,
    applyScope,
    moneyCompact,
    money,
    type Period,
} from '../services/analysis/model';
import { buildCsv, downloadCsv, yyyymmdd } from '../utils/csv';

const PERIODS: { key: Period; label: string; shortcut: string }[] = [
    { key: 'last12m', label: 'Last 12 months', shortcut: '1' },
    { key: 'fyToDate', label: 'This financial year', shortcut: '2' },
    { key: 'all', label: 'All time', shortcut: '3' },
];

const TRADE_FILTERS: { id: string; label: string }[] = [
    { id: 'all', label: 'All Trades' },
    { id: 'roofing', label: 'Roofing' },
    { id: 'balcony', label: 'Balcony' },
    { id: 'waterproofing', label: 'Waterproofing' },
    { id: 'concrete', label: 'Spalling & Concrete' },
    { id: 'painting', label: 'Painting' },
    { id: 'ceiling', label: 'Ceilings' },
    { id: 'gutter', label: 'Gutters' },
];

export default function AnalysisPage({
    projects,
    projectsLoaded,
    projectsLoadError = '',
    onRefreshProjects,
    onNavigate,
    onLoadProject,
    onOpenQuote,
    onOutcomeRecorded,
}: any) {
    const [period, setPeriod] = useState<Period>(BUILTIN_REPORT.period);
    const [query, setQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    // What is being proved. `cell: null` proves the whole panel from its stamp.
    const [proving, setProving] = useState<{ def: PanelDef; cell: Cell | null } | null>(null);

    // One derivation pass for the whole board, memoised on the projects array.
    // Every panel then reads the same rows, so no two panels can disagree about
    // a project's value, lane or age.
    const ctx = useMemo<AnalysisContext>(() => {
        const now = Date.now();
        let allRows = deriveRows(Array.isArray(projects) ? projects : [], now);
        if (selectedCategory !== 'all') {
            allRows = allRows.filter((r) => r.categories.includes(selectedCategory));
        }
        const rows = applyScope(allRows, period, query, now);
        return {
            rows,
            allRows,
            now,
            window: buildWindowStamp(allRows),
            period,
            query,
        };
    }, [projects, period, query, selectedCategory]);

    // ── Executive KPI benchmarks (Pass 1) ──────────────────────────────────
    // Honestly computed from ctx.rows. Never fabricates ratios on thin data.
    const kpis = useMemo(() => {
        const activeRows = ctx.rows.filter(
            (r) => r.lane === 'finish' || r.lane === 'send' || r.lane === 'waiting',
        );
        const activePipeline = activeRows.reduce((sum, r) => sum + r.value, 0);

        const decidedRows = ctx.rows.filter(
            (r) => r.lane === 'decided' || r.outcome === 'won' || r.outcome === 'lost',
        );
        const wonRows = decidedRows.filter((r) => r.outcome === 'won');
        const decidedCount = decidedRows.length;
        // Pinned by QuoteMaker honesty contract: < 3 decided quotes is noise.
        const winRate = decidedCount >= 3 ? ((wonRows.length / decidedCount) * 100).toFixed(1) : null;

        const pricedRows = ctx.rows.filter((r) => r.value > 0);
        const avgQuoteSize =
            pricedRows.length > 0
                ? pricedRows.reduce((sum, r) => sum + r.value, 0) / pricedRows.length
                : 0;

        const turnarounds = ctx.rows
            .map((r) => {
                const raw = r.raw;
                const outcome = r.outcome;
                const sentRaw =
                    raw?.sentAt ||
                    raw?.sent_at ||
                    ((outcome === 'sent' || outcome === 'won' || outcome === 'lost' || outcome === 'expired')
                        ? raw?.statusChangedAt || raw?.status_changed_at
                        : null);
                const sentMs = sentRaw ? new Date(sentRaw).getTime() : NaN;
                if (Number.isFinite(r.createdMs) && Number.isFinite(sentMs) && sentMs >= r.createdMs) {
                    return Math.max(0, Math.round((sentMs - r.createdMs) / 86400000));
                }
                return null;
            })
            .filter((d): d is number => d !== null);

        const avgTurnaround =
            turnarounds.length > 0
                ? (turnarounds.reduce((a, b) => a + b, 0) / turnarounds.length).toFixed(1)
                : null;

        return {
            activePipeline,
            activeCount: activeRows.length,
            winRate,
            wonCount: wonRows.length,
            decidedCount,
            avgQuoteSize,
            pricedCount: pricedRows.length,
            avgTurnaround,
            turnaroundCount: turnarounds.length,
        };
    }, [ctx.rows]);

    // ── Global Keyboard Shortcuts (Pass 4) ────────────────────────────────
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const el = document.activeElement as HTMLElement | null;
            const typing =
                el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
            if (typing) {
                if (e.key === 'Escape') el?.blur();
                return;
            }
            if (e.key === '1') {
                e.preventDefault();
                setPeriod('last12m');
            } else if (e.key === '2') {
                e.preventDefault();
                setPeriod('fyToDate');
            } else if (e.key === '3') {
                e.preventDefault();
                setPeriod('all');
            } else if (e.key === '/') {
                e.preventDefault();
                const input = document.querySelector<HTMLInputElement>('.an-filter__input');
                input?.focus();
                input?.select();
            } else if (e.key === 'Escape') {
                if (proving) {
                    setProving(null);
                } else if (query) {
                    setQuery('');
                } else if (selectedCategory !== 'all') {
                    setSelectedCategory('all');
                }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [proving, query, selectedCategory]);

    // ── Export CSV Handler (Pass 3) ───────────────────────────────────────
    const handleExportCsv = () => {
        const headers = [
            'ID',
            'Strata Plan',
            'Quote Ref',
            'Title',
            'Address',
            'Client',
            'Lane',
            'Outcome',
            'Value (Ex GST)',
            'Value (Inc GST)',
            'Created Date',
            'Categories',
        ];
        const exportRows = ctx.rows.map((r) => {
            const sp = r.raw?.formData?.strataplan || r.raw?.strataPlan || '';
            const quoteNo = r.quoteNumber || '';
            const valueEx = Math.round(r.value);
            const incGst = r.raw?.quoteData?.isGstInclusive ?? r.raw?.scopeGstInclusive ?? true;
            const valueInc = Math.round(incGst ? r.value * 1.1 : r.value);
            const createdDate = r.createdMs ? new Date(r.createdMs).toLocaleDateString('en-AU') : '';
            const cats = r.categories.join('; ');
            return {
                'ID': r.id,
                'Strata Plan': sp,
                'Quote Ref': quoteNo,
                'Title': r.title,
                'Address': r.address,
                'Client': r.client,
                'Lane': r.lane,
                'Outcome': r.outcome || 'draft',
                'Value (Ex GST)': valueEx,
                'Value (Inc GST)': valueInc,
                'Created Date': createdDate,
                'Categories': cats,
            };
        });
        const csvContent = buildCsv(headers, exportRows);
        downloadCsv(`QuoteMaker-Analysis-${yyyymmdd()}.csv`, csvContent);
    };

    const handlePrint = () => {
        window.print();
    };

    const report = BUILTIN_REPORT;
    const periodLabel = PERIODS.find((p) => p.key === period)?.label || 'All time';
    const retryProjects = () => {
        try {
            Promise.resolve(onRefreshProjects?.()).catch(() => undefined);
        } catch {
            // App owns the visible error string. A synchronous retry failure
            // leaves this state in place just like a rejected request.
        }
    };

    if (!projectsLoaded) {
        return (
            <div className="an-page">
                <div role="banner" className="an-bar">
                    <div className="an-bar__id"><h1>Analysis</h1></div>
                </div>
                <div className="an-board" aria-busy="true">
                    {[12, 6, 6, 6].map((span, i) => (
                        <div key={i} className="an-panel an-panel--skeleton" data-span={span} />
                    ))}
                </div>
            </div>
        );
    }

    if (projectsLoadError && ctx.window.n === 0) {
        return (
            <div className="an-page">
                <div role="banner" className="an-bar">
                    <div className="an-bar__id">
                        <h1>Analysis</h1>
                        <span className="an-chip">Quote book intelligence</span>
                    </div>
                </div>
                <div className="an-board an-board--empty">
                    <section className="an-panel an-panel--empty an-panel--error" data-span={12} role="alert">
                        <span className="an-empty-state__icon" aria-hidden="true"><Icons.AlertTriangle size={22} /></span>
                        <div className="an-empty-state__copy">
                            <h2>The quote book could not be loaded</h2>
                            <p>{projectsLoadError} No empty-project conclusion has been drawn.</p>
                            {onRefreshProjects && (
                                <div className="an-empty-state__actions">
                                    <button type="button" className="qm-inline-action-btn an-empty-state__primary" onClick={retryProjects}>
                                        Retry
                                    </button>
                                </div>
                            )}
                        </div>
                        <span className="an-stamp">Analysis is unavailable until the project list can be read</span>
                    </section>
                </div>
            </div>
        );
    }

    if (ctx.window.n === 0) {
        return (
            <div className="an-page">
                <div role="banner" className="an-bar">
                    <div className="an-bar__id">
                        <h1>Analysis</h1>
                        <span className="an-chip">Quote book intelligence</span>
                    </div>
                </div>
                <div className="an-board an-board--empty">
                    <section className="an-panel an-panel--empty" data-span={12}>
                        <span className="an-empty-state__icon" aria-hidden="true"><Icons.BarChart size={22} /></span>
                        <div className="an-empty-state__copy">
                            <h2>Nothing to analyse yet</h2>
                            <p>
                                Analysis builds itself from your quote book. Start or import a project and this page will track pipeline, win rate, response time and trade mix automatically.
                            </p>
                            <div className="an-empty-state__actions">
                                <button type="button" className="qm-inline-action-btn an-empty-state__primary" onClick={() => onNavigate?.('dashboard')}>
                                    <Icons.ArrowLeft size={13} aria-hidden="true" />
                                    Open Run Sheet
                                </button>
                            </div>
                        </div>
                        <span className="an-stamp">Waiting for the first saved project</span>
                    </section>
                </div>
            </div>
        );
    }

    return (
        <div className="an-page" data-stale={projectsLoadError ? 'true' : undefined}>
            {/* <div role="banner">, never <header>: the global stylesheet paints
                every <header> with a dark gradient and !important, which would
                render this light bar's ink text invisible. HARD RULE 1. */}
            <div role="banner" className="an-bar">
                <div className="an-bar__id">
                    <h1>Analysis</h1>
                    <span className="an-chip">{report.name}</span>
                </div>

                <div className="an-meta">
                    {ctx.window.n} project{ctx.window.n === 1 ? '' : 's'}
                    {ctx.window.capped ? ' (capped)' : ''}
                    {' · '}{moneyCompact(ctx.window.totalValue)} ex GST
                    {' · '}{ctx.window.periodLabel}
                </div>

                {/* The input opts out of the global input theme, so this pill owes
                    WCAG 2.4.11 its own :focus-within ring. HARD RULE 4. */}
                <div className="an-filter">
                    <Icons.Search size={13} aria-hidden="true" />
                    <input
                        className="qm-input-unthemed an-filter__input"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={rjBirdOrDemoText('Filter: balcony, Waratah, SP52758...', 'Filter: balcony, a street, a strata plan...')}
                        aria-label="Filter the board by trade, client, address, quote or strata plan number"
                    />
                    {query && (
                        <button
                            type="button"
                            className="qm-inline-action-btn an-filter__clear"
                            onClick={() => setQuery('')}
                            aria-label="Clear the filter"
                        ><Icons.X size={12} /></button>
                    )}
                </div>

                <div className="an-bar__actions">
                    {PERIODS.map((p) => (
                        <button
                            key={p.key}
                            type="button"
                            className="qm-inline-action-btn an-btn"
                            data-on={p.key === period ? 'true' : undefined}
                            onClick={() => setPeriod(p.key)}
                            aria-pressed={p.key === period}
                            title={`${p.label} (Press ${p.shortcut})`}
                        >{p.label}</button>
                    ))}
                </div>
            </div>

            {/* ── Subbar: Trade Chips + Intelligence Hub Actions (Passes 2 & 3) ── */}
            <div className="an-subbar">
                <div className="an-trade-chips" role="toolbar" aria-label="Filter by trade">
                    {TRADE_FILTERS.map((tf) => (
                        <button
                            key={tf.id}
                            type="button"
                            className="qm-inline-action-btn an-trade-chip"
                            data-active={selectedCategory === tf.id ? 'true' : undefined}
                            onClick={() => setSelectedCategory(tf.id)}
                            aria-pressed={selectedCategory === tf.id}
                        >
                            {tf.label}
                        </button>
                    ))}
                </div>

                <div className="an-hub-actions">
                    <button
                        type="button"
                        className="qm-inline-action-btn an-btn an-btn--hub"
                        onClick={handleExportCsv}
                        title="Download filtered quotes as CSV spreadsheet"
                        aria-label="Export CSV"
                    >
                        <Icons.Download size={13} aria-hidden="true" />
                        <span>Export CSV</span>
                    </button>
                    <button
                        type="button"
                        className="qm-inline-action-btn an-btn an-btn--hub"
                        onClick={handlePrint}
                        title="Print or save as PDF report"
                        aria-label="Print / PDF View"
                    >
                        <Icons.Printer size={13} aria-hidden="true" />
                        <span>Print</span>
                    </button>
                    <div className="an-shortcut-legend" aria-hidden="true">
                        <kbd>1-3</kbd> period <kbd>/</kbd> search <kbd>Esc</kbd>
                    </div>
                </div>
            </div>

            {/* ── Executive KPI Benchmark Ribbon (Pass 1) ────────────────────── */}
            <div className="an-kpi-ribbon" role="region" aria-label="Executive KPI Benchmarks">
                <div className="an-kpi-tile">
                    <span className="an-kpi-tile__label">Active Pipeline</span>
                    <div className="an-kpi-tile__val">
                        {moneyCompact(kpis.activePipeline)} <span className="an-kpi-tile__gst">ex GST</span>
                    </div>
                    <span className="an-kpi-tile__sub">
                        {kpis.activeCount} active quote{kpis.activeCount === 1 ? '' : 's'}
                    </span>
                </div>
                <div className="an-kpi-tile">
                    <span className="an-kpi-tile__label">Decided Win Rate</span>
                    <div className="an-kpi-tile__val" data-thin={kpis.winRate === null ? 'true' : undefined}>
                        {kpis.winRate !== null ? `${kpis.winRate}%` : '<3 decided'}
                    </div>
                    <span className="an-kpi-tile__sub">
                        {kpis.winRate !== null
                            ? `${kpis.wonCount} won · ${kpis.decidedCount} decided`
                            : 'Arithmetic on noise'}
                    </span>
                </div>
                <div className="an-kpi-tile">
                    <span className="an-kpi-tile__label">Avg Quote Size</span>
                    <div className="an-kpi-tile__val">
                        {moneyCompact(kpis.avgQuoteSize)} <span className="an-kpi-tile__gst">ex GST</span>
                    </div>
                    <span className="an-kpi-tile__sub">
                        {kpis.pricedCount} priced quote{kpis.pricedCount === 1 ? '' : 's'}
                    </span>
                </div>
                <div className="an-kpi-tile">
                    <span className="an-kpi-tile__label">Turnaround Velocity</span>
                    <div className="an-kpi-tile__val">
                        {kpis.avgTurnaround !== null ? `${kpis.avgTurnaround}d` : '—'}
                    </div>
                    <span className="an-kpi-tile__sub">
                        {kpis.avgTurnaround !== null
                            ? `Mean across ${kpis.turnaroundCount} sent`
                            : 'No send dates recorded'}
                    </span>
                </div>
            </div>

            {projectsLoadError && (
                <div className="an-data-warning" role="alert">
                    <Icons.AlertTriangle size={15} aria-hidden="true" />
                    <span><strong>Showing the last loaded quote book.</strong> {projectsLoadError}</span>
                    {onRefreshProjects && (
                        <button type="button" className="qm-inline-action-btn an-btn" onClick={retryProjects}>Retry</button>
                    )}
                </div>
            )}

            <div className="an-board">
                {report.panels.map((entry) => {
                    const def: PanelDef | undefined = ANALYSIS_PANELS[entry.id];
                    if (!def) return null;
                    // respectsPeriod:false panels read the unfiltered set, and their
                    // head shows an "all time" chip so the operator never wonders
                    // why they did not move when the period changed.
                    const panelCtx: AnalysisContext = def.respectsPeriod
                        ? ctx
                        : { ...ctx, rows: ctx.allRows };
                    const series = def.compute(panelCtx);
                    return (
                        <AnalysisPanel
                            key={def.id}
                            def={def}
                            series={series}
                            ctx={panelCtx}
                            span={entry.span}
                            onDrill={(d, c) => setProving({ def: d, cell: c })}
                            onStamp={(d) => setProving({ def: d, cell: null })}
                        />
                    );
                })}
            </div>

            {proving && (
                <EvidenceDrawer
                    def={proving.def}
                    cell={proving.cell}
                    ctx={ctx}
                    onClose={() => setProving(null)}
                    onNavigate={onNavigate}
                    onLoadProject={onLoadProject}
                    onOpenQuote={onOpenQuote}
                    onOutcomeRecorded={onOutcomeRecorded}
                />
            )}

            <span className="sr-only" aria-live="polite">
                Showing {ctx.rows.length} of {ctx.window.n} projects, {periodLabel.toLowerCase()}
                {query ? `, filtered by ${query}` : ''}.
            </span>
        </div>
    );
}
